import React, { useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  BomMatchResults,
  ModuleMatchResult,
  InverterMatchResult,
  OptimizerMatchResult,
  MatchConfidence,
  AuroraBomItem,
  ReviewedItemSelections,
} from '../../../utils/aurora-mapper.utils';
import {
  ModuleBrand,
  SolarModule,
  InverterBrand,
  Inverter,
  InverterType,
  Optimizer,
} from '../../../types';
import { SearchDropdown } from './SearchDropdown';
import { ConfirmationModal, ConfirmationType } from './ConfirmationModal';

// --- Internal state types ---

interface ModuleEditState {
  brandId: string;
  moduleId: string;
}

interface InverterEditState {
  type: InverterType;
  brandId: string;
  inverterId: string;
}

interface OptimizerEditState {
  optimizerId: string;
}

// --- Props ---

interface AuroraItemReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reviewed: ReviewedItemSelections, removedIds: Set<string>) => void;
  onSkipToStructures?: () => void;
  matchResults: BomMatchResults;
  moduleBrands: ModuleBrand[];
  solarModules: SolarModule[];
  inverterBrands: InverterBrand[];
  inverters: Inverter[];
  optimizers: Optimizer[];
  embedded?: boolean;
}

// --- Inverter type options ---

const INVERTER_TYPE_OPTIONS = [
  { id: InverterType.INVERTER, label: 'Inverter' },
  { id: InverterType.MICROINVERTER, label: 'Micro' },
];

// --- Section icon paths ---

const SECTION_ICONS: Record<string, string> = {
  modules: 'M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z',
  inverters: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z',
  optimizers: 'M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75',
  combiners: 'M6 6.878V6a2.25 2.25 0 012.25-2.25h7.5A2.25 2.25 0 0118 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 004.5 9v.878m13.5-3A2.25 2.25 0 0119.5 9v.878m0 0a2.246 2.246 0 00-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0121 12v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6c0-.98.626-1.813 1.5-2.122',
};

// --- Category section header ---

const SectionHeader: React.FC<{ sectionKey: string; count: number; matchedCount: number }> = ({
  sectionKey, count, matchedCount,
}) => {
  const icon = SECTION_ICONS[sectionKey];
  const allMatched = matchedCount === count;

  return (
    <div className="flex items-center justify-between py-2.5 px-4 3xl:py-3 3xl:px-5 rounded-t-xl border border-b-0 border-slate-200/80 bg-gradient-to-r from-slate-50 to-slate-100/80">
      <div className="flex items-center gap-2.5">
        <svg className="w-4 h-4 3xl:w-5 3xl:h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d={icon} />
        </svg>
        <span className="text-[11px] 3xl:text-xs font-bold uppercase tracking-widest text-slate-600">{sectionKey}</span>
        <span className="text-[10px] 3xl:text-[11px] font-semibold px-1.5 py-0.5 rounded-md border bg-slate-100 text-slate-600 border-slate-200">{count}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className={`w-2 h-2 rounded-full ${allMatched ? 'bg-emerald-500' : 'bg-amber-500'}`} />
        <span className={`text-[11px] 3xl:text-xs font-semibold ${allMatched ? 'text-emerald-600' : 'text-amber-600'}`}>
          {matchedCount}/{count} matched
        </span>
      </div>
    </div>
  );
};

// --- Confidence badge ---

const ConfidenceBadge: React.FC<{ confidence: MatchConfidence }> = ({ confidence }) => {
  const styles = {
    [MatchConfidence.EXACT]: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    [MatchConfidence.FUZZY]: 'bg-amber-50 text-amber-700 border-amber-200',
    [MatchConfidence.NONE]: 'bg-red-50 text-red-600 border-red-200',
  };
  const labels = {
    [MatchConfidence.EXACT]: 'Matched',
    [MatchConfidence.FUZZY]: 'Suggested',
    [MatchConfidence.NONE]: 'No Match',
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 3xl:px-2 3xl:py-1 rounded text-[9px] 3xl:text-[10px] font-semibold border ${styles[confidence]}`}>
      {confidence !== MatchConfidence.NONE && (
        <span className={`w-1.5 h-1.5 rounded-full mr-1 ${confidence === MatchConfidence.EXACT ? 'bg-emerald-500' : 'bg-amber-500'}`} />
      )}
      {confidence === MatchConfidence.NONE && (
        <svg className="w-2.5 h-2.5 3xl:w-3 3xl:h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01" />
        </svg>
      )}
      {labels[confidence]}
    </span>
  );
};

// --- Remove button (shared) ---

const RemoveButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex-shrink-0 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
    title="Remove item"
  >
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  </button>
);

// --- Aurora source info (shared left column) ---

const AuroraSourceInfo: React.FC<{
  bomItem: AuroraBomItem;
  extraBadge?: string;
}> = ({ bomItem, extraBadge }) => (
  <div className="w-[200px] 3xl:w-[240px] flex-shrink-0">
    <div className="text-[10px] 3xl:text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Aurora</div>
    <div className="text-[11px] 3xl:text-xs font-medium text-slate-700 truncate" title={bomItem.name}>{bomItem.name}</div>
    <div className="flex items-center gap-1.5 text-[10px] 3xl:text-[11px] text-slate-400 truncate">
      <span className="truncate">{bomItem.manufacturer_name}</span>
      <span className="text-slate-300">&middot;</span>
      <span className="text-slate-500 font-mono flex-shrink-0">Qty: {bomItem.quantity}</span>
      {extraBadge && (
        <span className="text-[9px] 3xl:text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium flex-shrink-0">{extraBadge}</span>
      )}
    </div>
  </div>
);

// --- Arrow divider ---

const ArrowDivider: React.FC<{ confidence: MatchConfidence }> = ({ confidence }) => (
  <div className="flex flex-col items-center flex-shrink-0 pt-3 gap-1">
    <ConfidenceBadge confidence={confidence} />
    <svg className="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
    </svg>
  </div>
);

// --- Row border color based on confidence ---

function rowBorderClass(confidence: MatchConfidence): string {
  if (confidence === MatchConfidence.NONE) return 'border-red-200 bg-red-50/30 border-l-[3px] border-l-red-400';
  if (confidence === MatchConfidence.FUZZY) return 'border-amber-200/60 bg-amber-50/20 border-l-[3px] border-l-amber-400';
  return 'border-slate-200/60 bg-white border-l-[3px] border-l-emerald-400';
}

// --- Module row ---

const ModuleRow: React.FC<{
  result: ModuleMatchResult;
  editState: ModuleEditState;
  onBrandChange: (brandId: string) => void;
  onModuleChange: (moduleId: string) => void;
  onRemove: () => void;
  moduleBrands: ModuleBrand[];
  solarModules: SolarModule[];
  isRemoved: boolean;
}> = ({ result, editState, onBrandChange, onModuleChange, onRemove, moduleBrands, solarModules, isRemoved }) => {
  const brandOptions = useMemo(() =>
    moduleBrands.map(b => ({ id: b.id, label: b.name })),
    [moduleBrands]
  );

  const brandValue = useMemo(() => {
    if (!editState?.brandId) return null;
    const brand = moduleBrands.find(b => b.id === editState.brandId);
    return brand ? { id: brand.id, label: brand.name } : null;
  }, [editState?.brandId, moduleBrands]);

  const moduleOptions = useMemo(() => {
    const filtered = editState?.brandId
      ? solarModules.filter(m => m.brands.some(b => b.id === editState.brandId))
      : solarModules;
    return filtered.map(m => ({ id: m.id, label: m.modelNumber, subtitle: `${m.wattage}W` }));
  }, [editState?.brandId, solarModules]);

  const moduleValue = useMemo(() => {
    if (!editState?.moduleId) return null;
    const mod = solarModules.find(m => m.id === editState.moduleId);
    return mod ? { id: mod.id, label: mod.modelNumber, subtitle: `${mod.wattage}W` } : null;
  }, [editState?.moduleId, solarModules]);

  if (isRemoved || !editState) return null;

  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${rowBorderClass(result.confidence)}`}>
      <AuroraSourceInfo bomItem={result.bomItem} />
      <ArrowDivider confidence={result.confidence} />
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <SearchDropdown
            options={brandOptions}
            onSelect={(opt) => onBrandChange(opt ? opt.id : '')}
            value={brandValue}
            placeholder="Select brand..."
            zIndex={10000}
          />
        </div>
        <div className="flex-1 min-w-0">
          <SearchDropdown
            options={moduleOptions}
            onSelect={(opt) => onModuleChange(opt ? opt.id : '')}
            value={moduleValue}
            placeholder="Select model..."
            zIndex={10000}
          />
        </div>
      </div>
      <div className="self-center">
        <RemoveButton onClick={onRemove} />
      </div>
    </div>
  );
};

// --- Inverter row ---

const InverterRow: React.FC<{
  result: InverterMatchResult;
  editState: InverterEditState;
  onTypeChange: (type: InverterType) => void;
  onBrandChange: (brandId: string) => void;
  onInverterChange: (inverterId: string) => void;
  onRemove: () => void;
  inverterBrands: InverterBrand[];
  inverters: Inverter[];
  isRemoved: boolean;
}> = ({ result, editState, onTypeChange, onBrandChange, onInverterChange, onRemove, inverterBrands, inverters, isRemoved }) => {
  const brandOptions = useMemo(() =>
    inverterBrands
      .filter(b => b.inverter_type === (editState?.type ?? InverterType.INVERTER))
      .map(b => ({ id: b.id, label: b.name })),
    [inverterBrands, editState?.type]
  );

  const brandValue = useMemo(() => {
    if (!editState?.brandId) return null;
    const brand = inverterBrands.find(b => b.id === editState.brandId);
    return brand ? { id: brand.id, label: brand.name } : null;
  }, [editState?.brandId, inverterBrands]);

  const inverterOptions = useMemo(() => {
    const filtered = editState?.brandId
      ? inverters.filter(inv => inv.manufacturer_id === editState.brandId)
      : inverters;
    return filtered.map(inv => ({
      id: inv.id,
      label: inv.model,
      subtitle: inv.wattage ? `${inv.wattage}W` : undefined,
    }));
  }, [editState?.brandId, inverters]);

  const inverterValue = useMemo(() => {
    if (!editState?.inverterId) return null;
    const inv = inverters.find(i => i.id === editState.inverterId);
    return inv ? { id: inv.id, label: inv.model, subtitle: inv.wattage ? `${inv.wattage}W` : undefined } : null;
  }, [editState?.inverterId, inverters]);

  if (isRemoved || !editState) return null;

  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${rowBorderClass(result.confidence)}`}>
      <AuroraSourceInfo
        bomItem={result.bomItem}
        extraBadge={result.componentType === 'microinverters' ? 'Micro' : 'Inverter'}
      />
      <ArrowDivider confidence={result.confidence} />
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <div className="w-[110px] flex-shrink-0">
          <SearchDropdown
            options={INVERTER_TYPE_OPTIONS}
            onSelect={(opt) => onTypeChange(opt ? opt.id as InverterType : InverterType.INVERTER)}
            value={INVERTER_TYPE_OPTIONS.find(o => o.id === editState.type) ?? INVERTER_TYPE_OPTIONS[0]}
            placeholder="Type..."
            zIndex={10000}
          />
        </div>
        <div className="flex-1 min-w-0">
          <SearchDropdown
            options={brandOptions}
            onSelect={(opt) => onBrandChange(opt ? opt.id : '')}
            value={brandValue}
            placeholder="Select brand..."
            zIndex={10000}
          />
        </div>
        <div className="flex-1 min-w-0">
          <SearchDropdown
            options={inverterOptions}
            onSelect={(opt) => onInverterChange(opt ? opt.id : '')}
            value={inverterValue}
            placeholder="Select model..."
            zIndex={10000}
          />
        </div>
      </div>
      <div className="self-center">
        <RemoveButton onClick={onRemove} />
      </div>
    </div>
  );
};

// --- Optimizer row ---

const OptimizerRow: React.FC<{
  result: OptimizerMatchResult;
  editState: OptimizerEditState;
  onOptimizerChange: (optimizerId: string) => void;
  onRemove: () => void;
  optimizers: Optimizer[];
  isRemoved: boolean;
}> = ({ result, editState, onOptimizerChange, onRemove, optimizers, isRemoved }) => {
  const optimizerOptions = useMemo(() =>
    optimizers.map(o => ({
      id: o.id,
      label: o.type,
      subtitle: o.current ? `${o.current}A` : undefined,
    })),
    [optimizers]
  );

  const optimizerValue = useMemo(() => {
    if (!editState?.optimizerId) return null;
    const opt = optimizers.find(o => o.id === editState.optimizerId);
    return opt ? { id: opt.id, label: opt.type, subtitle: opt.current ? `${opt.current}A` : undefined } : null;
  }, [editState?.optimizerId, optimizers]);

  if (isRemoved || !editState) return null;

  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${rowBorderClass(result.confidence)}`}>
      <AuroraSourceInfo bomItem={result.bomItem} />
      <ArrowDivider confidence={result.confidence} />
      <div className="flex-1 min-w-0 flex items-center">
        <SearchDropdown
          options={optimizerOptions}
          onSelect={(opt) => onOptimizerChange(opt ? opt.id : '')}
          value={optimizerValue}
          placeholder="Select optimizer..."
          zIndex={10000}
        />
      </div>
      <div className="self-center">
        <RemoveButton onClick={onRemove} />
      </div>
    </div>
  );
};

// --- Combiner row (read-only, just remove) ---

const CombinerRow: React.FC<{
  bomItem: AuroraBomItem;
  onRemove: () => void;
  isRemoved: boolean;
}> = ({ bomItem, onRemove, isRemoved }) => {
  if (isRemoved) return null;

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 3xl:px-4 3xl:py-3 rounded-lg border border-slate-200/60 bg-white">
      <div className="flex-1 min-w-0">
        <div className="text-[11px] 3xl:text-xs font-medium text-slate-700 truncate">{bomItem.name}</div>
        <div className="text-[10px] 3xl:text-[11px] text-slate-400">{bomItem.manufacturer_name} &middot; Qty: {bomItem.quantity}</div>
      </div>
      <span className="inline-flex items-center px-1.5 py-0.5 3xl:px-2 3xl:py-1 rounded text-[9px] 3xl:text-[10px] font-semibold border bg-slate-50 text-slate-500 border-slate-200">
        Pass-through
      </span>
      <RemoveButton onClick={onRemove} />
    </div>
  );
};

// --- Main modal ---

export const AuroraItemReviewModal: React.FC<AuroraItemReviewModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onSkipToStructures,
  matchResults,
  moduleBrands,
  solarModules,
  inverterBrands,
  inverters,
  optimizers,
  embedded = false,
}) => {
  // Edit state for modules: keyed by bomItem.id
  const [moduleEdits, setModuleEdits] = useState<Record<string, ModuleEditState>>(() => {
    const state: Record<string, ModuleEditState> = {};
    matchResults.modules.forEach(r => {
      state[r.bomItem.id] = {
        brandId: r.matchedBrand?.id ?? '',
        moduleId: r.matchedModule?.id ?? '',
      };
    });
    return state;
  });

  // Edit state for inverters
  const [inverterEdits, setInverterEdits] = useState<Record<string, InverterEditState>>(() => {
    const state: Record<string, InverterEditState> = {};
    matchResults.inverters.forEach(r => {
      state[r.bomItem.id] = {
        type: r.componentType === 'microinverters' ? InverterType.MICROINVERTER : InverterType.INVERTER,
        brandId: r.matchedBrand?.id ?? '',
        inverterId: r.matchedInverter?.id ?? '',
      };
    });
    return state;
  });

  // Edit state for optimizers
  const [optimizerEdits, setOptimizerEdits] = useState<Record<string, OptimizerEditState>>(() => {
    const state: Record<string, OptimizerEditState> = {};
    matchResults.optimizers.forEach(r => {
      state[r.bomItem.id] = {
        optimizerId: r.matchedOptimizer?.id ?? '',
      };
    });
    return state;
  });

  // Removed items
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // Reset state when modal opens with new data
  React.useEffect(() => {
    if (isOpen) {
      const mState: Record<string, ModuleEditState> = {};
      matchResults.modules.forEach(r => {
        mState[r.bomItem.id] = { brandId: r.matchedBrand?.id ?? '', moduleId: r.matchedModule?.id ?? '' };
      });
      setModuleEdits(mState);

      const iState: Record<string, InverterEditState> = {};
      matchResults.inverters.forEach(r => {
        iState[r.bomItem.id] = {
          type: r.componentType === 'microinverters' ? InverterType.MICROINVERTER : InverterType.INVERTER,
          brandId: r.matchedBrand?.id ?? '',
          inverterId: r.matchedInverter?.id ?? '',
        };
      });
      setInverterEdits(iState);

      const oState: Record<string, OptimizerEditState> = {};
      matchResults.optimizers.forEach(r => {
        oState[r.bomItem.id] = { optimizerId: r.matchedOptimizer?.id ?? '' };
      });
      setOptimizerEdits(oState);

      setRemovedIds(new Set());
      setDeleteTarget(null);
    }
  }, [isOpen, matchResults]);

  // --- Module handlers ---
  const handleModuleBrandChange = useCallback((bomId: string, brandId: string) => {
    setModuleEdits(prev => ({ ...prev, [bomId]: { ...prev[bomId], brandId, moduleId: '' } }));
  }, []);

  const handleModuleChange = useCallback((bomId: string, moduleId: string) => {
    setModuleEdits(prev => ({ ...prev, [bomId]: { ...prev[bomId], moduleId } }));
  }, []);

  // --- Inverter handlers ---
  const handleInverterTypeChange = useCallback((bomId: string, type: InverterType) => {
    setInverterEdits(prev => ({ ...prev, [bomId]: { ...prev[bomId], type, brandId: '', inverterId: '' } }));
  }, []);

  const handleInverterBrandChange = useCallback((bomId: string, brandId: string) => {
    setInverterEdits(prev => ({ ...prev, [bomId]: { ...prev[bomId], brandId, inverterId: '' } }));
  }, []);

  const handleInverterChange = useCallback((bomId: string, inverterId: string) => {
    setInverterEdits(prev => ({ ...prev, [bomId]: { ...prev[bomId], inverterId } }));
  }, []);

  // --- Optimizer handler ---
  const handleOptimizerChange = useCallback((bomId: string, optimizerId: string) => {
    setOptimizerEdits(prev => ({ ...prev, [bomId]: { ...prev[bomId], optimizerId } }));
  }, []);

  // --- Remove handlers ---
  const handleRequestRemove = useCallback((id: string, name: string) => {
    setDeleteTarget({ id, name });
  }, []);

  const handleConfirmRemove = useCallback(() => {
    if (deleteTarget) {
      setRemovedIds(prev => new Set([...Array.from(prev), deleteTarget.id]));
      setDeleteTarget(null);
    }
  }, [deleteTarget]);

  // --- Confirm (proceed to Step 2) ---
  const handleConfirm = useCallback(() => {
    const moduleMap = new Map<string, { brand: ModuleBrand | null; module: SolarModule | null }>();
    matchResults.modules.forEach(r => {
      const edit = moduleEdits[r.bomItem.id];
      const brand = moduleBrands.find(b => b.id === edit?.brandId) ?? null;
      const mod = solarModules.find(m => m.id === edit?.moduleId) ?? null;
      moduleMap.set(r.bomItem.id, { brand, module: mod });
    });

    const inverterMap = new Map<string, { brand: InverterBrand | null; inverter: Inverter | null; componentType: 'inverters' | 'microinverters' }>();
    matchResults.inverters.forEach(r => {
      const edit = inverterEdits[r.bomItem.id];
      const brand = inverterBrands.find(b => b.id === edit?.brandId) ?? null;
      const inv = inverters.find(i => i.id === edit?.inverterId) ?? null;
      const componentType = edit?.type === InverterType.MICROINVERTER ? 'microinverters' as const : 'inverters' as const;
      inverterMap.set(r.bomItem.id, { brand, inverter: inv, componentType });
    });

    const optimizerMap = new Map<string, { optimizer: Optimizer | null }>();
    matchResults.optimizers.forEach(r => {
      const edit = optimizerEdits[r.bomItem.id];
      const opt = optimizers.find(o => o.id === edit?.optimizerId) ?? null;
      optimizerMap.set(r.bomItem.id, { optimizer: opt });
    });

    onConfirm({
      modules: moduleMap,
      inverters: inverterMap,
      optimizers: optimizerMap,
      removedIds,
    }, removedIds);
  }, [matchResults, moduleEdits, inverterEdits, optimizerEdits, moduleBrands, solarModules, inverterBrands, inverters, optimizers, removedIds, onConfirm]);

  // --- Summary counts ---
  const matchedModules = matchResults.modules.filter(r => !removedIds.has(r.bomItem.id) && moduleEdits[r.bomItem.id]?.moduleId).length;
  const matchedInverters = matchResults.inverters.filter(r => !removedIds.has(r.bomItem.id) && inverterEdits[r.bomItem.id]?.inverterId).length;
  const matchedOptimizers = matchResults.optimizers.filter(r => !removedIds.has(r.bomItem.id) && optimizerEdits[r.bomItem.id]?.optimizerId).length;

  const activeModules = matchResults.modules.filter(r => !removedIds.has(r.bomItem.id));
  const activeInverters = matchResults.inverters.filter(r => !removedIds.has(r.bomItem.id));
  const activeOptimizers = matchResults.optimizers.filter(r => !removedIds.has(r.bomItem.id));
  const activeCombiners = matchResults.combiners.filter(c => !removedIds.has(c.id));

  const totalActive = activeModules.length + activeInverters.length + activeOptimizers.length + activeCombiners.length;
  const totalMatched = matchedModules + matchedInverters + matchedOptimizers + activeCombiners.length;

  if (!isOpen) return null;

  const content = (
    <>
      {/* Header */}
      <div className="px-5 pt-3 pb-3 3xl:px-6 3xl:pt-4 3xl:pb-4 border-b border-slate-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 3xl:w-10 3xl:h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-sm">
              <svg className="w-5 h-5 3xl:w-6 3xl:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm 3xl:text-base font-semibold text-slate-800">Review Item Matching</h3>
              <p className="text-[11px] 3xl:text-xs text-slate-400">Verify Aurora BOM items are mapped to the correct database entries</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[10px] 3xl:text-[11px] uppercase tracking-wider font-semibold text-slate-400">Items</div>
              <div className="text-sm 3xl:text-base font-mono font-semibold">
                <span className="text-emerald-600">{totalMatched}</span>
                <span className="text-slate-300">/</span>
                <span className="text-slate-600">{totalActive}</span>
                <span className="text-[10px] 3xl:text-[11px] text-slate-400 ml-1">matched</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {/* Modules */}
        {activeModules.length > 0 && (
          <div>
            <SectionHeader
              sectionKey="modules"
              count={activeModules.length}
              matchedCount={matchedModules}
            />
            <div className="border border-slate-200/70 rounded-b-xl p-2.5 space-y-2">
              {matchResults.modules.map(r => (
                <ModuleRow
                  key={r.bomItem.id}
                  result={r}
                  editState={moduleEdits[r.bomItem.id]}
                  onBrandChange={(brandId) => handleModuleBrandChange(r.bomItem.id, brandId)}
                  onModuleChange={(moduleId) => handleModuleChange(r.bomItem.id, moduleId)}
                  onRemove={() => handleRequestRemove(r.bomItem.id, r.bomItem.name)}
                  moduleBrands={moduleBrands}
                  solarModules={solarModules}
                  isRemoved={removedIds.has(r.bomItem.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Inverters */}
        {activeInverters.length > 0 && (
          <div>
            <SectionHeader
              sectionKey="inverters"
              count={activeInverters.length}
              matchedCount={matchedInverters}
            />
            <div className="border border-slate-200/70 rounded-b-xl p-2.5 space-y-2">
              {matchResults.inverters.map(r => (
                <InverterRow
                  key={r.bomItem.id}
                  result={r}
                  editState={inverterEdits[r.bomItem.id]}
                  onTypeChange={(type) => handleInverterTypeChange(r.bomItem.id, type)}
                  onBrandChange={(brandId) => handleInverterBrandChange(r.bomItem.id, brandId)}
                  onInverterChange={(inverterId) => handleInverterChange(r.bomItem.id, inverterId)}
                  onRemove={() => handleRequestRemove(r.bomItem.id, r.bomItem.name)}
                  inverterBrands={inverterBrands}
                  inverters={inverters}
                  isRemoved={removedIds.has(r.bomItem.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Optimizers */}
        {activeOptimizers.length > 0 && (
          <div>
            <SectionHeader
              sectionKey="optimizers"
              count={activeOptimizers.length}
              matchedCount={matchedOptimizers}
            />
            <div className="border border-slate-200/70 rounded-b-xl p-2.5 space-y-2">
              {matchResults.optimizers.map(r => (
                <OptimizerRow
                  key={r.bomItem.id}
                  result={r}
                  editState={optimizerEdits[r.bomItem.id]}
                  onOptimizerChange={(optimizerId) => handleOptimizerChange(r.bomItem.id, optimizerId)}
                  onRemove={() => handleRequestRemove(r.bomItem.id, r.bomItem.name)}
                  optimizers={optimizers}
                  isRemoved={removedIds.has(r.bomItem.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Combiners */}
        {activeCombiners.length > 0 && (
          <div>
            <SectionHeader
              sectionKey="combiners"
              count={activeCombiners.length}
              matchedCount={activeCombiners.length}
            />
            <div className="border border-slate-200/70 rounded-b-xl p-2.5 space-y-2">
              {matchResults.combiners.map(c => (
                <CombinerRow
                  key={c.id}
                  bomItem={c}
                  onRemove={() => handleRequestRemove(c.id, c.name)}
                  isRemoved={removedIds.has(c.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-3.5 3xl:px-6 3xl:py-4 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
        <p className="text-[10px] 3xl:text-[11px] text-slate-400">
          {removedIds.size > 0 && <span className="text-amber-600 font-medium">{removedIds.size} item(s) removed &middot; </span>}
          Review matches and edit selections as needed
        </p>
        <div className="flex gap-2.5">
          <button
            onClick={onClose}
            className="px-3.5 py-1.5 3xl:px-4 3xl:py-2 text-xs 3xl:text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-lg border border-slate-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-1.5 3xl:px-5 3xl:py-2 text-xs 3xl:text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-lg shadow-sm shadow-emerald-500/20 transition-all duration-150 flex items-center gap-2"
          >
            Confirm Matching
            <svg className="w-3.5 h-3.5 3xl:w-4 3xl:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Delete confirmation */}
      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmRemove}
        title="Remove Item"
        message={`Remove "${deleteTarget?.name}" from the mapping? This item will not be included in the system configuration.`}
        type={ConfirmationType.DANGER}
        confirmButtonText="Remove"
        cancelButtonText="Keep"
      />
    </>
  );

  if (embedded) return content;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl 3xl:max-w-6xl mx-auto shadow-2xl border border-gray-100 overflow-hidden flex flex-col h-[85vh]">
        {content}
      </div>
    </div>,
    document.body
  );
};
